import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';
import { exportToCSV } from '../../utils/csv';

interface DailyReport {
  id: string;
  userId: string;
  userName: string;
  department: string;
  reportDate: string;
  workContent: string;
  workHours: number;
  overtime: number;
  isEmergency: boolean;
  status: string;
  createdAt: string;
}


interface DailyReportStats {
  todaySubmitted: number;
  todayTotal: number;
  emergencyCount: number;
  averageOvertime: number;
}

interface User {
  id: string;
  name: string;
}

interface Department {
  id: string;
  name: string;
}

const initialFormData = {
  reportDate: new Date().toISOString().split('T')[0],
  workContent: '',
  achievements: '',
  challenges: '',
  tomorrowPlan: '',
  workHours: 8,
  overtime: 0,
  isEmergency: false,
  emergencyContent: '',
};

export default function DailyReportList() {
  const { user } = useAuth();
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });

  // クイックアクション: ?action=new でモーダルを自動で開く（create権限チェック）
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      if (canCreate('daily_reports')) setShowModal(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  // ページネーション・ソート・表示件数（URLから取得）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const setCurrentPage = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.delete('page'); return prev; });
  };
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'reportDate',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });

  // 統計
  const [stats, setStats] = useState<DailyReportStats>({
    todaySubmitted: 0,
    todayTotal: 0,
    emergencyCount: 0,
    averageOvertime: 0,
  });

  const isManager = user?.role === 'admin' || user?.role === 'system_admin' || user?.role === 'manager';

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'userId', label: '報告者', type: 'select', options: users.map(u => ({ value: u.id, label: u.name })) },
    { key: 'department', label: '部署', type: 'select', options: departments.map(d => ({ value: d.id, label: d.name })) },
    { key: 'reportDate', label: '報告日', type: 'date' },
    { key: 'workHours', label: '勤務時間', type: 'number', placeholder: '時間' },
    { key: 'overtime', label: '残業時間', type: 'number', placeholder: '時間' },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'draft', label: '下書き' },
      { value: 'submitted', label: '提出済' },
      { value: 'reviewed', label: '承認済' },
      { value: 'rejected', label: '却下' },
    ]},
    { key: 'isEmergency', label: '緊急報告', type: 'boolean' },
  ], [users, departments]);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [searchConditions, currentPage, pageSize, sortConfig]);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active&limit=1000');
      setUsers(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setUsers([]);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await api.get<{ items: Department[] }>('/api/masters/departments');
      setDepartments(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setDepartments([]);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', String(currentPage));
      params.append('pageSize', String(pageSize));
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      
      const data = await api.get<{ items: DailyReport[]; total: number; totalPages: number }>(
        `/api/daily-reports?${params.toString()}`
      );
      setReports(Array.isArray(data?.items) ? data.items : []);
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch daily reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await api.get<DailyReportStats>('/api/daily-reports/stats');
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setSearchParams(prev => { prev.delete('page'); return prev; });
  };

  const defaultSort = { key: 'createdAt', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      let next: { key: string; direction: 'asc' | 'desc' };
      if (prev.key !== key) next = { key, direction: 'asc' };
      else if (prev.direction === 'asc') next = { key, direction: 'desc' };
      else next = defaultSort;
      setSearchParams(p => { p.set('sortBy', next.key); p.set('sortOrder', next.direction); p.delete('page'); return p; }, { replace: true });
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent, isDraft: boolean = false) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post('/api/daily-reports', {
        ...formData,
        status: isDraft ? 'draft' : 'submitted',
      });
      setShowModal(false);
      setFormData(initialFormData);
      fetchReports();
      fetchStats();
    } catch (error) {
      console.error('Failed to create daily report:', error);
      toast.error('日報の作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    const csvColumns = [
      { key: 'reportDate' as const, header: '日付', format: (v: unknown) => new Date(v as string).toLocaleDateString('ja-JP') },
      { key: 'userName' as const, header: '報告者' },
      { key: 'department' as const, header: '部署' },
      { key: 'workContent' as const, header: '業務内容' },
      { key: 'workHours' as const, header: '勤務時間', format: (v: unknown) => `${v}` },
      { key: 'overtime' as const, header: '残業時間', format: (v: unknown) => `${v}` },
      { key: 'isEmergency' as const, header: '緊急', format: (v: unknown) => v ? 'はい' : 'いいえ' },
      { key: 'status' as const, header: 'ステータス', format: (v: unknown) => {
        const labels: Record<string, string> = { draft: '下書き', submitted: '提出済', reviewed: '承認済', rejected: '却下' };
        return labels[v as string] || String(v);
      }},
    ];
    exportToCSV(reports as unknown as Record<string, unknown>[], csvColumns, '日報一覧');
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'warning' | 'success' | 'danger'> = {
      draft: 'default',
      submitted: 'warning',
      reviewed: 'success',
      rejected: 'danger',
    };
    const labels: Record<string, string> = {
      draft: '下書き',
      submitted: '提出済',
      reviewed: '承認済',
      rejected: '却下',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  const columns = [
    { 
      key: 'reportDate', 
      header: '日付', 
      minWidth: '100px',
      sortable: true,
      mobileTitle: true,
      render: (report: DailyReport) => (
        <Link 
          to={`/daily-reports/${report.id}`}
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          {new Date(report.reportDate).toLocaleDateString('ja-JP')}
          <span className="block text-xs text-gray-500 md:hidden">{report.userName}</span>
        </Link>
      )
    },
    { key: 'userName', header: '報告者', minWidth: '120px', sortable: true, hideOnMobile: true },
    { key: 'department', header: '部署', minWidth: '100px', hideOnMobile: true },
    { 
      key: 'workContent', 
      header: '業務内容', 
      minWidth: '200px', 
      render: (report: DailyReport) => (
        <div className="flex items-center gap-2">
          {report.isEmergency && (
            <span className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center" title="緊急報告">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
              </svg>
            </span>
          )}
          <span className="line-clamp-1">{report.workContent}</span>
        </div>
      )
    },
    { 
      key: 'workHours', 
      header: '勤務', 
      minWidth: '70px',
      sortable: true,
      render: (report: DailyReport) => `${report.workHours}h`
    },
    { 
      key: 'overtime', 
      header: '残業', 
      minWidth: '70px',
      sortable: true,
      render: (report: DailyReport) => (
        <span className={report.overtime > 0 ? 'text-orange-600 dark:text-orange-400 font-medium' : ''}>
          {report.overtime}h
        </span>
      )
    },
    { 
      key: 'status', 
      header: 'ステータス', 
      minWidth: '100px', 
      render: (report: DailyReport) => getStatusBadge(report.status) 
    },
    { 
      key: 'actions', 
      header: '', 
      minWidth: '80px', 
      render: (report: DailyReport) => (
        <Link to={`/daily-reports/${report.id}`}>
          <Button variant="ghost" size="sm">詳細</Button>
        </Link>
      )
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">日報管理</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleExportCSV} className="flex-1 sm:flex-none">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            CSV
          </Button>
          {canCreate('daily_reports') && (
            <Button onClick={() => setShowModal(true)} className="flex-1 sm:flex-none">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              日報を作成
            </Button>
          )}
        </div>
      </div>

      {/* 統計カード */}
      {isManager && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">本日の提出</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.todaySubmitted}<span className="text-base font-normal text-gray-500">/{stats.todayTotal}</span>
            </p>
          </Card>
          <Card className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">提出率</p>
            <p className="text-2xl font-bold text-blue-600">
              {stats.todayTotal > 0 ? Math.round((stats.todaySubmitted / stats.todayTotal) * 100) : 0}%
            </p>
          </Card>
          <Card className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">緊急報告</p>
            <p className={`text-2xl font-bold ${stats.emergencyCount > 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
              {stats.emergencyCount}件
            </p>
          </Card>
          <Card className="text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">平均残業</p>
            <p className={`text-2xl font-bold ${stats.averageOvertime > 2 ? 'text-orange-600' : 'text-gray-900 dark:text-white'}`}>
              {stats.averageOvertime.toFixed(1)}h
            </p>
          </Card>
        </div>
      )}

      {/* テーブル */}
      <Card>
        {/* 詳細検索 */}
        <div className="mb-4">
          <AdvancedSearch
            fields={searchFields}
            onSearch={handleSearch}
            quickSearchPlaceholder="業務内容で検索..."
            quickSearchField="workContent"
            initialConditions={searchConditions}
          />
        </div>
        
        <Table 
          columns={columns} 
          data={reports} 
          loading={loading}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        
        {/* ページネーション */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
            <span className="text-gray-400">/ {totalItems}件</span>
          </div>
          <div className="order-1 sm:order-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </Card>

      {/* 作成モーダル */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="日報を作成" size="lg">
        <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-4">
          <Input
            label="日付"
            type="date"
            value={formData.reportDate}
            onChange={(e) => setFormData({ ...formData, reportDate: e.target.value })}
            required
          />
          
          {/* 緊急報告 */}
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={formData.isEmergency}
                onChange={(e) => setFormData({ ...formData, isEmergency: e.target.checked })}
                className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
              <span className="text-sm font-medium text-red-600">緊急報告がある</span>
            </label>
            {formData.isEmergency && (
              <textarea
                value={formData.emergencyContent}
                onChange={(e) => setFormData({ ...formData, emergencyContent: e.target.value })}
                className="w-full px-3 py-2 border border-red-300 dark:border-red-600 rounded-lg focus:ring-2 focus:ring-red-500 dark:bg-gray-800 text-gray-900 dark:text-white"
                rows={3}
                placeholder="緊急の内容を入力してください"
                required={formData.isEmergency}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              業務内容 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.workContent}
              onChange={(e) => setFormData({ ...formData, workContent: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={4}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">成果・進捗</label>
            <textarea
              value={formData.achievements}
              onChange={(e) => setFormData({ ...formData, achievements: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">課題・問題点</label>
            <textarea
              value={formData.challenges}
              onChange={(e) => setFormData({ ...formData, challenges: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">明日の予定</label>
            <textarea
              value={formData.tomorrowPlan}
              onChange={(e) => setFormData({ ...formData, tomorrowPlan: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={2}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="勤務時間"
              type="number"
              step="0.5"
              value={formData.workHours}
              onChange={(e) => setFormData({ ...formData, workHours: parseFloat(e.target.value) })}
              required
            />
            <Input
              label="残業時間"
              type="number"
              step="0.5"
              value={formData.overtime}
              onChange={(e) => setFormData({ ...formData, overtime: parseFloat(e.target.value) })}
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setShowModal(false)} 
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              キャンセル
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={(e) => handleSubmit(e, true)} 
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              {submitting ? '保存中...' : '下書き保存'}
            </Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? '提出中...' : '提出'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
